<template>
  <div class="tab-content-inner">
    <div class="info-alert">
      <CIcon name="cil-info" class="info-icon" />
      <div>
        <strong>Dicionário de Termos da IA:</strong> mapeia termos informais, abreviações e
        sinônimos para os nomes oficiais do catálogo. Quando o cliente digita "coca", a IA
        entende que se trata de "Coca-Cola" — o que melhora a precisão das sugestões de
        pedido e das buscas por voz e texto.
      </div>
    </div>

    <!--
      Exemplos de DE/PARA.

      O dicionário começa vazio em toda instalação, e vazio ele não explica o
      que se espera de cada coluna: a tela mostra "termo" e "termo canônico", e
      quem chega não descobre por aí que o primeiro é como o cliente fala e o
      segundo é o nome que existe no catálogo. Quem já entendeu recolhe o
      bloco, e ele continua recolhido nas próximas visitas: a didática é para
      quem precisa dela.
    -->
    <div v-if="exemplos.length" class="exemplos">
      <button
        type="button"
        class="exemplos__cabecalho"
        :aria-expanded="exemplosAbertos ? 'true' : 'false'"
        @click="alternarExemplos"
      >
        <CIcon :name="exemplosAbertos ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
        Como preencher: exemplos de DE/PARA
      </button>

      <div v-show="exemplosAbertos" class="exemplos__corpo">
        <p class="exemplos__intro">
          À esquerda, como o cliente fala; à direita, o nome que existe no seu catálogo.
          Os exemplos são de ramos diferentes de propósito — a ideia é reconhecer o
          padrão e traduzir para os seus produtos.
        </p>

        <table class="exemplos__tabela">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Como o cliente fala</th>
              <th>Nome no catálogo</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="linha in linhasDeExemplo"
              :key="linha.tipo + linha.de"
              :class="{ 'exemplos__tabela--grupo': linha.primeiraDoTipo }"
            >
              <td class="celula-tipo">
                <template v-if="linha.primeiraDoTipo">
                  <span class="celula-tipo__nome">{{ linha.titulo }}</span>
                  <span class="celula-tipo__ajuda">{{ linha.explicacao }}</span>
                </template>
              </td>
              <td class="celula-de">{{ linha.de }}</td>
              <td class="celula-para">{{ linha.para }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="toolbar-actions">
      <CButton
        color="primary"
        size="sm"
        @click="showWizard = true"
      >
        <CIcon name="cil-lightbulb" class="mr-1" />
        Testar &amp; Treinar IA
      </CButton>
    </div>

    <TreinoChatIA
      :show.sync="showWizard"
      @trained="onTrained"
    />

    <PainelTermosNaoResolvidos
      ref="painelNaoResolvidos"
      @updated="onTrained"
    />

    <crud-base
      ref="crud"
      url="/admin/dicionario-ia"
      :columns="columns"
      :onDelete="() => {}"
      :onUpdate="() => {}"
      :onStore="() => {}"
      :formInputs="formInputs"
      :enableSearch="true"
      primaryKey="id"
      :filters="filters"
      :useQueryString="false"
      :enableImport="true"
      importTitle="Importar Termos em Massa"
      :importTemplate="importTemplate"
    >
      <template v-slot:import-info>
        <div class="import-info-alert">
          <CIcon name="cil-info" class="import-info-icon" />
          <div>
            Envie um arquivo <strong>.csv</strong> seguindo o template.
            Colunas obrigatórias: <code>termo</code>, <code>termo_canonico</code>, <code>tipo</code>.
            <br />
            Tipos válidos: dimensao, marca, fornecedor, produto, sinonimo.
          </div>
        </div>
      </template>

      <template v-slot:filter-search>
        <select
          class="form-control form-control-sm filter-select"
          v-model="filters.tipo"
        >
          <option value="">Todos os tipos</option>
          <option
            v-for="t in tipos"
            :key="t.value"
            :value="t.value"
          >
            {{ t.label }}
          </option>
        </select>
      </template>

      <template v-slot:tipo="{model}">
        <span class="badge-tipo" :class="'tipo-' + model.tipo">
          {{ getLabelTipo(model.tipo) }}
        </span>
      </template>
    </crud-base>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import CrudBase from "@/components/crud/CrudBase";
import TreinoChatIA from "./TreinoChatIA.vue";
import PainelTermosNaoResolvidos from "./PainelTermosNaoResolvidos.vue";

const CHAVE_EXEMPLOS = "dicionario-ia:exemplos-abertos";

/*
 * Aberto por padrão: a primeira visita é justamente a de quem não sabe o que
 * preencher. A escolha de recolher é que vale a pena lembrar.
 */
function lerPreferenciaDeExemplos() {
  try {
    return localStorage.getItem(CHAVE_EXEMPLOS) !== "0";
  } catch (erro) {
    return true;
  }
}

function gravarPreferenciaDeExemplos(aberto) {
  try {
    localStorage.setItem(CHAVE_EXEMPLOS, aberto ? "1" : "0");
  } catch (erro) {
    // Preferência é conforto: sem onde guardar, segue aberto.
  }
}

export default {
  name: "tab-dicionario",
  components: {
    CrudBase,
    TreinoChatIA,
    PainelTermosNaoResolvidos,
  },
  data() {
    return {
      showWizard: false,
      exemplos: [],
      exemplosAbertos: lerPreferenciaDeExemplos(),
      filters: {
        tipo: "",
      },
      tipos: [
        { value: "dimensao", label: "Dimensão" },
        { value: "marca", label: "Marca" },
        { value: "fornecedor", label: "Fornecedor" },
        { value: "produto", label: "Produto" },
        { value: "sinonimo", label: "Sinônimo" },
      ],
      columns: [
        { label: "Termo", field: "termo", sortable: false },
        { label: "Termo Canônico", field: "termo_canonico", sortable: false },
        { label: "Tipo", field: "tipo", sortable: false, useSlot: true },
        { label: "Cadastrado por", field: "autor_nome", sortable: false },
      ],
    };
  },
  computed: {
    /*
     * Os grupos chegam aninhados e saem achatados: numa tabela só, o olho
     * compara as duas colunas de cima a baixo. Em cartões lado a lado — como
     * era —, cada tipo repetia o cabeçalho e a explicação, e o bloco ficava
     * maior que o cadastro que ele vinha explicar.
     */
    linhasDeExemplo() {
      const linhas = [];

      this.exemplos.forEach((grupo) => {
        grupo.linhas.forEach((linha, indice) => {
          linhas.push({
            tipo: grupo.tipo,
            titulo: grupo.titulo,
            explicacao: grupo.explicacao,
            primeiraDoTipo: indice === 0,
            de: linha.de,
            para: linha.para,
          });
        });
      });

      return linhas;
    },

    /*
     * O arquivo de exemplo sai dos mesmos exemplos mostrados na tela. Eram
     * duas listas escritas à mão, e divergiram: quem lia a tela e quem baixava
     * o modelo aprendiam com produtos diferentes.
     */
    importTemplate() {
      const linhas = [];

      this.exemplos.forEach((grupo) => {
        grupo.linhas.forEach((linha) => {
          linhas.push(`${linha.de};${linha.para};${grupo.tipo};;`);
        });
      });

      /*
       * Sem exemplos não há modelo: o arquivo sairia só com o cabeçalho, e
       * quem baixasse concluiria que o formato é esse. O botão some com o
       * valor nulo, que é o que o componente de listagem já testa.
       */
      if (!linhas.length) {
        return null;
      }

      return {
        header: "termo;termo_canonico;tipo;dimensao_tipo;dimensao_id",
        rows: linhas,
        filename: "template_dicionario_ia.csv",
      };
    },

    formInputs() {
      return {
        termo: {
          type: "string",
          ui: {
            label: "Termo",
            columns: 12,
            placeholder: "Ex: coca, pepsi, skol...",
          },
          rules: {
            required: { value: true, errMsg: "Campo obrigatório" },
          },
        },
        termo_canonico: {
          type: "string",
          ui: {
            label: "Termo Canônico",
            columns: 12,
            placeholder: "Ex: Coca-Cola, Pepsi-Cola, Skol...",
          },
          rules: {
            required: { value: true, errMsg: "Campo obrigatório" },
          },
        },
        tipo: {
          type: "string",
          default: "",
          ui: {
            widget: "select",
            label: "Tipo",
            columns: 12,
            widgetConfig: {
              enumSource: [
                { label: "Selecione...", value: "" },
                ...this.tipos.map((t) => ({
                  label: t.label,
                  value: t.value,
                })),
              ],
            },
          },
        },
        dimensao_tipo: {
          type: "string",
          ui: {
            label: "Dimensão Tipo",
            columns: 6,
            placeholder: "Ex: embalagem, tamanho...",
            hidden: "dx: {{$root.tipo}} !== 'dimensao'",
          },
        },
        dimensao_id: {
          type: "number",
          ui: {
            label: "Dimensão ID",
            columns: 6,
            placeholder: "ID da dimensão",
            hidden: "dx: {{$root.tipo}} !== 'dimensao'",
          },
        },
      };
    },
  },
  methods: {
    alternarExemplos() {
      this.exemplosAbertos = !this.exemplosAbertos;
      gravarPreferenciaDeExemplos(this.exemplosAbertos);
    },
    carregarExemplos() {
      get("/admin/dicionario-ia/exemplos")
        .then((resposta) => {
          this.exemplos = (resposta && resposta.data) || [];
        })
        .catch(() => {
          // Exemplos são didática, não funcionalidade: sem eles o cadastro
          // continua inteiro.
          this.exemplos = [];
        });
    },
    getLabelTipo(tipo) {
      const found = this.tipos.find((t) => t.value === tipo);
      return found ? found.label : tipo;
    },
    onTrained() {
      // Recarregar lista do CRUD após treino
      if (this.$refs.crud) {
        this.$refs.crud.loadData();
      }
    },
  },
  mounted() {
    this.carregarExemplos();
  },
};
</script>

<style scoped>
.tab-content-inner {
  padding: 20px;
}

.exemplos {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  margin-bottom: 16px;
  background: #fff;
}

.exemplos__cabecalho {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  cursor: pointer;
  text-align: left;
}

.exemplos__corpo {
  padding: 0 14px 14px;
}

.exemplos__intro {
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
  margin-bottom: 12px;
  max-width: 760px;
}

.exemplos__tabela {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.exemplos__tabela th {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #98a2b3;
  text-align: left;
  padding: 0 12px 6px 0;
  border-bottom: 1px solid #eef0f3;
}

.exemplos__tabela td {
  padding: 5px 12px 5px 0;
  vertical-align: top;
  /* O tema desenha uma linha em cada célula de tabela; aqui ela separaria os
     exemplos de um mesmo tipo, que é justamente o que se quer junto. */
  border: none;
}

/* A linha que abre um tipo ganha o traço: separa os grupos sem precisar de um
   cabeçalho por grupo, que era o que inchava o bloco. */
.exemplos__tabela--grupo td {
  border-top: 1px solid #eef0f3;
  padding-top: 10px;
}

.exemplos__tabela tbody tr:first-child td {
  border-top: none;
}

.celula-tipo {
  width: 30%;
  min-width: 170px;
}

.celula-tipo__nome {
  display: block;
  font-weight: 600;
  color: #344054;
}

.celula-tipo__ajuda {
  display: block;
  color: #98a2b3;
  line-height: 1.4;
}

.celula-de {
  width: 28%;
  color: #667085;
}

.celula-para {
  color: #344054;
  font-weight: 600;
}

.info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}

.toolbar-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.filter-select {
  width: auto;
  min-width: 180px;
}

.badge-tipo {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.tipo-dimensao {
  background: #edf8f9;
  color: #2298a3;
}

.tipo-marca {
  background: #f2f2f8;
  color: #3a3e81;
}

.tipo-fornecedor {
  background: #fff3e0;
  color: #e65100;
}

.tipo-produto {
  background: #e8f5e9;
  color: #2e7d32;
}

.tipo-sinonimo {
  background: #fce4ec;
  color: #c62828;
}

.import-info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
}

.import-info-alert code {
  background: #fff;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.import-info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}
</style>
